import { proxy, useSnapshot } from 'valtio';

import { type PaywallFeatureState, PaywallFeature } from '@canvas/paywall-types';

export const ALL_FEATURES: readonly PaywallFeature[] = Object.values(PaywallFeature);

/**
 * Paywall features that have been added to the entitlement templates
 * DO NOT ADD HERE UNLESS ENTITLEMENT FEATURES HAVE BEEN CONFIRMED TO BE PRESENT
 * IN ALL STAGING AND PROD SITES - failure to do so will cause on call to be paged
 * @see https://hello.atlassian.net/wiki/spaces/CAN/pages/3135810403/How+to+Add+a+paywall+feature+gate Runbook
 * @example The following is an example for adding features to HAMS
 * https://stash.atlassian.com/projects/PUR/repos/entitlement-template/pull-requests/332/overview
 * make sure the PRs have been processed by commerce
 * @example The following is an example for adding features to CCP https://hello.atlassian.net/browse/CCPS-1591
 * make sure the PRs have been processed by CCP
 */
export const RELEASED_PAYWALL_FEATURES: readonly PaywallFeature[] = [
  /**
   * @see https://stash.atlassian.com/projects/PUR/repos/entitlement-template/pull-requests/332/overview
   * ENTS / HAMS
   * @see https://hello.atlassian.net/browse/CCPS-1591 OC / CCP
   */
  PaywallFeature.SMART_CONNECTORS,

  /**
   * @see https://stash.atlassian.com/projects/PUR/repos/entitlement-template/pull-requests/332/overview
   * ENTS / HAMS
   * @see https://hello.atlassian.net/browse/CCPS-1591 OC / CCP
   */
  PaywallFeature.SMART_SECTIONS,

  /**
   * AI paywalled features are shimmed and do not originate directly from HAMS / CCP
   * These are not released yet
   */
  // PaywallFeature.AI_GENERATE,
  // PaywallFeature.AI_CLUSTERING,
];

const defaultPaywallFeatureState: PaywallFeatureState = {
  // Pre-beta features are enabled by default to ensure
  // beta users aren't disrupted as we build out paywall
  [PaywallFeature.SMART_CONNECTORS]: {
    isEntitled: true,
  },
  [PaywallFeature.SMART_SECTIONS]: {
    isEntitled: true,
  },
  // post-beta features are not enabled by default
  [PaywallFeature.AI_GENERATE]: {
    isEntitled: false,
  },
  [PaywallFeature.AI_CLUSTERING]: {
    isEntitled: false,
  },
};

// WARNING: Do not use this state outside of this file
// Reading from this state will circumvent the paywall rollout feature flag
// check (see `checkIfFeatureIsEnabled`)
// Do not export this state in paywall.ts
export const currentPaywallFeatureState = proxy({ ...defaultPaywallFeatureState });

const checkIfFeatureIsEnabled = (state: PaywallFeatureState, feature: PaywallFeature) => {
  return state[feature]?.isEntitled ?? false;
};

/** Returns `true` if feature is not locked by paywall, otherwise returns `false` */
export const isPaywallUnlocked = (feature: PaywallFeature) =>
  checkIfFeatureIsEnabled(currentPaywallFeatureState, feature);

/**
 *  Hook version of `isPaywallUnlocked`
 *
 *  Returns `true` if feature is not locked by paywall, otherwise returns `false`
 */
export const useIsPaywallUnlocked = (feature: PaywallFeature) => {
  const paywallFeatureState = useSnapshot(currentPaywallFeatureState);

  return checkIfFeatureIsEnabled(paywallFeatureState, feature);
};

export const resetPaywallFeatureState = () => {
  for (const paywallFeature of Object.keys(
    defaultPaywallFeatureState
  ) as PaywallFeature[]) {
    currentPaywallFeatureState[paywallFeature] =
      defaultPaywallFeatureState[paywallFeature];
  }
};
